import IconButton from 'components/@extended/IconButton';
import Tooltip from 'components/@extended/Tooltip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { ArrowUpwardRounded, CircleAlert, CircleCheck, LockIcon, PlusIcon, Trash2Icon } from 'components/icons';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Button } from 'components/ui/button';
import { TableCell, TableRow } from 'components/ui/table';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, UseFormWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { ShareRateDisplay } from './ShareInput';

/** The box around a payer or payment-distribution table, as the sections of the invoice tabs draw theirs. */
export const PAYER_TABLE_FRAME_CLASS = 'border-divider overflow-hidden rounded-md border';
export const PAYER_TABLE_CLASS = 'w-full text-left text-sm';
/** The payer tables sit inside a form card, so their cells are tighter on the x axis than the app's simple tables. */
const COMPACT_X_CLASS = 'px-2 first:pl-3 last:pr-3';
export const PAYER_CELL_CLASS = cn(SIMPLE_TABLE_CELL_CLASS, COMPACT_X_CLASS);
export const PAYER_HEAD_CLASS = cn(SIMPLE_TABLE_HEAD_CLASS, COMPACT_X_CLASS);
export const NUMERIC_CELL_CLASS = cn(PAYER_CELL_CLASS, 'text-right');
export const NUMERIC_HEAD_CLASS = cn(PAYER_HEAD_CLASS, 'text-right');
export const ACTIONS_CELL_CLASS = cn(PAYER_CELL_CLASS, 'py-2 text-right');
export const FOOTER_CELL_CLASS = cn(
    'text-body1 border-border bg-grey-50 border-t py-4 font-semibold tabular-nums',
    COMPACT_X_CLASS
);
// A kit table cell draws its links as plain text until hovered; a read-only payer name is the way to its contact,
// so it reads as a link at rest. `:where` keeps the deceased-contact tint winning over it.
export const READ_ONLY_LINK_CELL_CLASS = '[&_:where(.entity-link)]:text-link!';

/** The share rates' total, green with a check when they add up to 100 and red with an alert otherwise. */
export function ShareTotalIndicator({ total, isComplete }: { total: number; isComplete: boolean }) {
    return (
        <span
            className={cn('inline-flex items-center justify-end gap-1', isComplete ? 'text-fg-success' : 'text-error')}
        >
            {isComplete ? <CircleCheck className="size-4" /> : <CircleAlert className="size-4" />}
            <ShareRateDisplay value={total} />
        </span>
    );
}

/** Rows 2..n of a payer list: the first row is the main payer, so moving a row to the top makes it the main one. */
export function MoveToTopButton({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <Tooltip text="invoices.payers.move-to-top">
            <IconButton type="button" size="small" aria-label={label} onClick={onClick}>
                <ArrowUpwardRounded className="size-4" />
            </IconButton>
        </Tooltip>
    );
}

export function RemovePayerButton({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <IconButton type="button" size="small" aria-label={label} onClick={onClick}>
            <Trash2Icon className="size-4" />
        </IconButton>
    );
}

/** The last body row of an editable payer table, spanning all its columns. */
export function AddPayerRow({ columnCount, onClick }: { columnCount: number; onClick: () => void }) {
    return (
        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
            <TableCell className={cn(PAYER_CELL_CLASS, 'py-1.5')} colSpan={columnCount}>
                <Button
                    type="button"
                    {...buttonStyle({ variant: 'text', size: 'extraSmall' }, '-ml-2 gap-1')}
                    onClick={onClick}
                >
                    <PlusIcon className="size-4" />
                    <FormattedMessage id="invoices.payers.add-payer" />
                </Button>
            </TableCell>
        </TableRow>
    );
}

/** A row of a payer at 0 %, which is listed but gets no invoice of its own. */
export const ZERO_SHARE_ROW_CLASS = '[&>td]:text-fg-secondary';

/** Why a payer table shows no inputs, spelled out under it rather than left to a tooltip. */
export function ReadOnlyPayersNote({ messageId }: { messageId: string }) {
    return (
        <p className="text-caption text-fg-secondary flex items-center gap-1.5">
            <LockIcon className="size-4 shrink-0" />
            <FormattedMessage id={messageId} />
        </p>
    );
}

/**
 * A payer row's contact dialog lists the parties the other rows hold too, so picking one there is refused with a
 * message rather than greyed out without a word. The message stays until the list next changes.
 */
export function useRefusedDuplicatePick<T extends FieldValues>(watch: UseFormWatch<T>) {
    const [isDuplicatePickRefused, setIsDuplicatePickRefused] = useState(false);

    useEffect(() => {
        if (!isDuplicatePickRefused) {
            return;
        }
        const { unsubscribe } = watch(() => setIsDuplicatePickRefused(false));
        return unsubscribe;
    }, [isDuplicatePickRefused, watch]);

    const refuseDuplicatePick = useCallback(() => setIsDuplicatePickRefused(true), []);
    return { isDuplicatePickRefused, refuseDuplicatePick };
}
